const _ = require("lodash");
const LRUCache = require("../../../shared/LRUCache");
const {getTTSHash} = require("../../../shared/TTSHash");
const Mixpanel = require("./Mixpanel");
const {critterToReadText} = require("../canvasmath/BatexAloud");

/**
 * ElevenLabs TTS provider using HTML5 Audio element.
 * Fetches MP3 from backend proxy endpoint.
 *
 * Features:
 * - LRU cache for audio blobs (keeps last 10 most recently used)
 * - Preloading support for fetching audio without playing
 * - Deduplication of in-flight requests
 * - Single autoplay tracking (only most recent speak() call auto-plays)
 */

const VOICE_ID = "ieAON9kcbhyylWIP4fSB"; // Fiona
const SPEED = 0.9;
const CACHE_SIZE = 10;
const NORMALIZE_NUMBER_COMMAS = false;

// Reverse mapping from BatexAloud hyphenated forms back to original critter names
// (ElevenLabs handles pronunciations externally)
const CRITTER_REVERSE_MAP = {
	..._.invert(critterToReadText),
	"hippo-pot-uh-moose": "hippopota moose",
	"pell-uh-camel": "peli camel",
	"rye-gnaws-uh-rabbit": "rhinocer rabbit",
};

// Pronunciation mappings for ElevenLabs TTS
const PRONUNCIATION_MAP = {
	...CRITTER_REVERSE_MAP,
	Xs: "exes",
	xs: "exes",
};

/**
 * Removes thousand-separator commas from numbers so that "1,234" and "1234"
 * produce the same TTS hash and audio. Matches commas that separate a group
 * of 1-3 digits from one or more groups of exactly 3 digits.
 *
 * Examples:
 *   "1,234"                    → "1234"
 *   "1,234,567"                → "1234567"   (millions)
 *   "1,234,567,890"            → "1234567890" (billions)
 *   "1,234,567,890,123"        → "1234567890123" (trillions)
 *   "The answer is 1,234"      → "The answer is 1234"
 *   "42"                       → "42"        (no change)
 *   "Hello, world"             → "Hello, world" (comma not in number)
 *   "$1,234.56"                → "$1234.56"
 *
 * Regex: \b(\d{1,3})((?:,\d{3})+)\b
 * - \b word boundary
 * - (\d{1,3}) initial 1–3 digits
 * - ((?:,\d{3})+) one or more ",NNN" groups
 * - \b word boundary
 *
 * @param {string} text
 * @returns {string}
 */
function normalizeNumberCommas(text) {
	if (typeof text !== "string") return text;
	// Remove commas that separate 1–3 digit group from groups of 3 digits
	return text.replace(
		/\b(\d{1,3})((?:,\d{3})+)\b/g,
		(_tmp, start, rest) => start + rest.replace(/,/g, "")
	);
}

// --- numbersToSpoken: convert number tokens to spoken words (mathematically correct) ---
const ONES = [
	"zero",
	"one",
	"two",
	"three",
	"four",
	"five",
	"six",
	"seven",
	"eight",
	"nine",
];
const TEENS = [
	"ten",
	"eleven",
	"twelve",
	"thirteen",
	"fourteen",
	"fifteen",
	"sixteen",
	"seventeen",
	"eighteen",
	"nineteen",
];
const TENS = [
	"",
	"",
	"twenty",
	"thirty",
	"forty",
	"fifty",
	"sixty",
	"seventy",
	"eighty",
	"ninety",
];

/** 0–99 as words */
function sayUnder100(n) {
	if (n < 10) return ONES[n];
	if (n < 20) return TEENS[n - 10];
	const tens = TENS[Math.floor(n / 10)];
	const ones = n % 10;
	return ones ? tens + "-" + ONES[ones] : tens;
}

/** 0–999 as words */
function sayUnder1000(n) {
	if (n === 0) return "";
	if (n < 100) return sayUnder100(n);
	const h = Math.floor(n / 100);
	const rest = n % 100;
	return ONES[h] + " hundred" + (rest ? " " + sayUnder100(rest) : "");
}

/** Integer 0–999,999,999,999,999 as words (up to 999 trillion) */
function sayInteger(n) {
	if (n === 0) return "zero";
	const scales = [
		[1e12, "trillion"],
		[1e9, "billion"],
		[1e6, "million"],
		[1e3, "thousand"],
	];
	const out = [];
	for (const [div, name] of scales) {
		const chunk = Math.floor(n / div);
		n %= div;
		if (chunk > 0) {
			out.push(sayUnder1000(chunk) + " " + name);
		}
	}
	if (n > 0) out.push(sayUnder1000(n));
	return out.join(" ");
}

/** Max integer we pronounce (999 trillion); above this leave token unchanged */
const MAX_SAYABLE_INT = 999999999999999;

/**
 * Convert a single number token (e.g. "42", "-007", "3.14") to spoken form.
 * @param {string} token
 * @returns {string}
 */
function numbersToSpokenToken(token) {
	const negative = token.startsWith("-");
	const t = negative ? token.slice(1) : token;
	const dotIdx = t.indexOf(".");
	const intPart = dotIdx >= 0 ? t.slice(0, dotIdx) : t;
	const decPart = dotIdx >= 0 ? t.slice(dotIdx + 1) : "";

	// Leading zeros (read out as "zero" each)
	let leadingZeros = "";
	let i = 0;
	while (i < intPart.length && intPart[i] === "0") {
		leadingZeros += "zero ";
		i++;
	}
	const restInt = intPart.slice(i);

	let prefix = negative ? "negative " : "";
	if (leadingZeros) prefix += leadingZeros;

	// Integer part after leading zeros
	if (restInt.length > 15) {
		// Above 999 trillion: leave unchanged
		return token;
	}
	const n = restInt === "" ? 0 : parseInt(restInt, 10);
	if (restInt !== "" && (n > MAX_SAYABLE_INT || Number.isNaN(n))) {
		return token;
	}
	let spoken;
	if (restInt === "") {
		// All zeros (e.g. "0", "00"); prefix already includes leadingZeros
		spoken = prefix.trim();
		if (spoken === "") spoken = "zero";
	} else {
		spoken = prefix + sayInteger(n);
	}

	// Decimal part: " point " then each digit
	if (decPart.length > 0) {
		const digitWords = decPart.split("").map(d => ONES[parseInt(d, 10)]);
		spoken += " point " + digitWords.join(" ");
	}

	return spoken.trim();
}

/**
 * Replaces number tokens in text with spoken-word form (mathematically correct).
 * Runs after normalizeNumberCommas. Whole tokens only (word-boundary style);
 * leading zeros read out, decimals as "point" then digit-by-digit, negatives as "negative".
 * Integers above 999 trillion are left unchanged.
 *
 * @param {string} text
 * @returns {string}
 */
function normalizeNumbersToText(text) {
	if (typeof text !== "string") return text;
	// Match number tokens: optional minus, leading zeros, digits, optional . and fractional digits
	// Preceded by start or non-word char, followed by non-word or end (whole tokens only)
	return text.replace(
		/(^|\W)(-?0*\d+\.?\d*)(?=\W|$)/g,
		(_match, prefix, num) => prefix + numbersToSpokenToken(num)
	);
}

/**
 * Apply pronunciation mappings to text before sending to TTS.
 * @param {string} text
 * @returns {string}
 */
function applyPronunciations(text) {
	let result = text;
	for (const [from, to] of Object.entries(PRONUNCIATION_MAP)) {
		result = result.split(from).join(to);
	}
	return result;
}

/**
 * @type {unknown}
 */
const ReadAloudElevenLabs = function() {
	this.audio = null;
	this.playing = false;
	this.paused = false;
	this.pending = false;
	this.listeners = [];

	// LRU cache: text -> blob URL
	this.audioCache = new LRUCache(CACHE_SIZE);
	// Revoke blob URLs when evicted from cache
	this.audioCache.onDestroy(url => {
		URL.revokeObjectURL(url);
	});

	// Map of text -> Promise for in-flight fetch requests
	this.pendingRequests = new Map();

	// The text that should auto-play when its fetch completes (only one at a time)
	this.autoplayText = null;

	// One-time Mixpanel track when AI ReadAloud is first used
	this.hasTrackedReadAloudEnabled = false;
};

ReadAloudElevenLabs.prototype.destroy = function() {
	this.cancel();
	this.listeners = [];
	this.audioCache.removeAll();
	this.pendingRequests.clear();
	this.autoplayText = null;
};

ReadAloudElevenLabs.prototype.isSpeaking = function() {
	return this.playing;
};

ReadAloudElevenLabs.prototype.isPaused = function() {
	return this.paused;
};

ReadAloudElevenLabs.prototype.isPending = function() {
	return this.pending;
};

ReadAloudElevenLabs.prototype.cancel = function() {
	if (this.audio) {
		this.audio.pause();
		this.audio.src = "";
		this.audio = null;
	}
	this.updateStatus({playing: false, paused: false});
	this.pending = false;
	this.autoplayText = null;
};

ReadAloudElevenLabs.prototype.pause = function() {
	if (this.audio && this.playing) {
		this.audio.pause();
		this.updateStatus({playing: false, paused: true});
	}
};

ReadAloudElevenLabs.prototype.resume = function() {
	if (this.audio && this.paused) {
		this.audio.play();
		this.updateStatus({playing: true, paused: false});
	}
};

ReadAloudElevenLabs.prototype.onStatusChange = function(listenerFunc) {
	this.listeners.push(listenerFunc);
};

ReadAloudElevenLabs.prototype.updateStatus = function(newStatus = {}) {
	const {playing = false, paused = false} = newStatus;
	if (this.playing === playing && this.paused === paused) return;
	this.playing = playing;
	this.paused = paused;
	_.each(this.listeners, listenerFunc => listenerFunc({playing, paused}));
};

/**
 * Internal helper to fetch audio from ElevenLabs and cache the result.
 * Deduplicates in-flight requests for the same text.
 * @param {string} text
 * @returns {Promise<string|null>} - resolves to blob URL on success, null on failure
 */
ReadAloudElevenLabs.prototype._fetchAudio = function(text) {
	if (!this.hasTrackedReadAloudEnabled) {
		this.hasTrackedReadAloudEnabled = true;
		Mixpanel.track("AI ReadAloud enabled");
	}

	const normalizedText = NORMALIZE_NUMBER_COMMAS
		? normalizeNumberCommas(text)
		: text;

	// Check if already cached (key by normalized text so "1,234" and "1234" share cache)
	if (this.audioCache.has(normalizedText)) {
		return Promise.resolve(this.audioCache.get(normalizedText));
	}

	// Check if already fetching
	if (this.pendingRequests.has(normalizedText)) {
		return this.pendingRequests.get(normalizedText);
	}

	// Start new fetch - try static cache first, fall back to API
	const textForTTS = applyPronunciations(
		normalizeNumbersToText(normalizedText)
	);
	const cacheKey = getTTSHash(textForTTS);
	const ttsAssetOrigin =
		typeof window !== "undefined" && window.TTS_DISTRIBUTION_DOMAIN_NAME
			? `https://${window.TTS_DISTRIBUTION_DOMAIN_NAME}`
			: "";
	const staticAssetUrl = ttsAssetOrigin
		? `${ttsAssetOrigin}/tts/${cacheKey}.mp3`
		: `/assets/tts/${cacheKey}.mp3`;
	const fetchPromise = fetch(staticAssetUrl)
		.then(response => {
			if (response.ok) return response.blob();
			// Fall back to API
			return fetch("/tts/elevenlabs", {
				method: "POST",
				headers: {"Content-Type": "application/json"},
				body: JSON.stringify({
					text: textForTTS,
					voiceId: VOICE_ID,
					speed: SPEED,
				}),
			}).then(r => (r.ok ? r.blob() : null));
		})
		.then(blob => {
			// Clean up pending request
			this.pendingRequests.delete(normalizedText);

			if (!blob) return null;

			const url = URL.createObjectURL(blob);
			this.audioCache.store(normalizedText, url);
			return url;
		})
		.catch(() => {
			this.pendingRequests.delete(normalizedText);
			return null;
		});

	this.pendingRequests.set(normalizedText, fetchPromise);
	return fetchPromise;
};

/**
 * Internal helper to play audio from a blob URL.
 * @param {string} url - blob URL
 * @returns {Promise<boolean>} - resolves true on success
 */
ReadAloudElevenLabs.prototype._playAudio = function(url) {
	// Reuse this.audio when speak() already primed it during the user tap (cache
	// miss path). Do not use new Audio(url): a fresh element is not linked to the
	// gesture, and iOS Safari rejects play() after async fetch (e.g. instructions
	// read-aloud on older iPads). Assign src on the existing element instead.
	if (this.audio) {
		this.audio.pause();
		this.audio.onended = null;
		this.audio.onerror = null;
	} else {
		this.audio = new Audio();
	}

	this.audio.onended = () => {
		this.updateStatus({playing: false, paused: false});
	};
	this.audio.onerror = () => {
		this.updateStatus({playing: false, paused: false});
	};
	this.audio.src = url;

	const playPromise = this.audio.play();
	if (playPromise && typeof playPromise.then === "function") {
		return playPromise
			.then(() => {
				this.updateStatus({playing: true, paused: false});
				return true;
			})
			.catch(() => {
				this.updateStatus({playing: false, paused: false});
				return false;
			});
	} else {
		this.updateStatus({playing: true, paused: false});
		return Promise.resolve(true);
	}
};

/**
 * Preloads audio for the given text without playing it.
 * Useful for preloading next problem's audio on problem switch.
 * @param {string} text
 * @returns {Promise<boolean>} - resolves true if audio is ready (cached or fetched)
 */
ReadAloudElevenLabs.prototype.preload = function(text) {
	if (!text) {
		return Promise.resolve(false);
	}
	const normalized = normalizeNumberCommas(text);
	return this._fetchAudio(normalized).then(url => !!url);
};

/**
 * Fetches audio from ElevenLabs via backend proxy and plays it.
 * Uses cache when available. If audio is being fetched, marks it for
 * autoplay when ready (only one text can be marked at a time).
 * @param {string} text
 * @returns {Promise<boolean>} - resolves true on success, false on failure
 */
ReadAloudElevenLabs.prototype.speak = function(text) {
	if (!text) {
		return Promise.resolve(false);
	}
	const normalized = normalizeNumberCommas(text);

	// Stop any current playback
	this.cancel();

	// Check if already cached - play immediately (key by normalized text)
	if (this.audioCache.has(normalized)) {
		const url = this.audioCache.get(normalized);
		return this._playAudio(url);
	}

	// Cache miss: create Audio and call play() now (no src yet) so iOS Safari
	// associates this element with the user gesture. _playAudio sets src later.
	this.audio = new Audio();
	const primePromise = this.audio.play();
	if (primePromise && typeof primePromise.then === "function") {
		primePromise.catch(() => {});
	}

	// Mark this text for autoplay (overwrites any previous)
	this.autoplayText = normalized;
	this.pending = true;

	// Fetch audio (will dedupe if already in flight)
	return this._fetchAudio(normalized).then(url => {
		// Check if this is still the text we want to autoplay
		if (this.autoplayText !== normalized) {
			// User moved on, don't play but return success since audio is cached
			return !!url;
		}

		this.pending = false;
		this.autoplayText = null;

		if (!url) {
			return false;
		}

		return this._playAudio(url);
	});
};

module.exports = ReadAloudElevenLabs;
module.exports.normalizeNumberCommas = normalizeNumberCommas;
module.exports.normalizeNumbersToText = normalizeNumbersToText;
